import type { MockOrderScenario, RecoveryConfig } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import type { RecoveryChatSource } from 'hooks/websocket/recoveryChatSource'
import { useRecoveryChat } from 'hooks/websocket/useRecoveryChat'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import type { Auth } from 'reducers/auth'
import { useStyletron } from 'styletron-react'
import AgentTuningConfigModals, {
  type RowKey,
} from '../components/AgentTuningConfigModals'
import { WIZARD_STICKY_BAR_Z_INDEX } from '../components/layout'
import type { WizardState } from '../Wizard/WizardContext'
import PlaybackNav from './PlaybackNav'
import SimulatorEngine from './SimulatorEngine'
import SimulatorStatus from './SimulatorStatus'
import { useCannedConversation } from './useCannedConversation'
import { usePendingConversationReset } from './usePendingConversationReset'
import { usePersonaPlayback } from './usePersonaPlayback'
import { useSimulatorData } from './useSimulatorData'

const t = buildTranslate('recovery_ai.sandbox')
// Persona copy lives one level down; personaScenarios' name_key values are keys
// RELATIVE to this namespace (ENG-971 renders name_key as the scenario title).
const tPersonas = buildTranslate('recovery_ai.sandbox.personas')

type Props = {
  /**
   * Swappable chat source (see StandaloneSimulator's docstring). Stable for the
   * instance lifetime — key the component to switch, never swap on a mounted one.
   */
  useChatSource?: RecoveryChatSource
  /**
   * Fires once the merchant completes the final guided persona. Wired to the
   * persona playback's Complete transition in U11 / AI-42; accepted now to lock
   * the contract WP4's wizard "Testing" step depends on. Coordinate this props
   * contract with WP4 (Wizard/).
   */
  onAllScenariosComplete: () => void
  /**
   * Applies a WizardState patch — the config write-through (mirror a live save so
   * Finalize can't clobber it) and the free-phase re-arm of `testingComplete`.
   * Passed EXPLICITLY by the one call site that owns the provider (Testing.tsx →
   * `update`); made a prop rather than reaching for `useWizardState` internally so
   * a provider-less mount can't silently no-op the write-through and restore the
   * Finalize clobber (Darshan review, #3878). Absent ⇒ no write-through (fine for
   * the standalone test mounts that don't exercise it).
   */
  onWizardPatch?: (patch: Partial<WizardState>) => void
  /**
   * Holds the guided phase's SCRIPTED transcript while the wizard's first
   * coach tour is still running (AI-921 sequencing). Scripted-only: flag off
   * there is no transcript to hold, so the live persona opener fires on its
   * own schedule — this stage is master's behaviour exactly. Default false,
   * so a bare mount plays exactly as it does today.
   *
   *   context: scheduling only. useTranscriptPlayback still emits frame 0, so
   *   a held phone shows the 2-message greeting the tour was written against,
   *   never an empty box.
   *   context: WHEN it lifts is the host's policy, not this component's —
   *   Testing.tsx releases when tour 1 ends: the merchant presses its last
   *   step's "Got it", dismisses it, or it never opens (Option A).
   */
  holdGuidedPlayback?: boolean
}

// Wizard-embedded simulator: renders the shared SimulatorEngine pre-activation
// with NO chrome (the teal stepper + Cancel/Continue belong to WP4's wizard
// shell, not here). It does not modify the wizard STEPS array.
const WizardSimulator = ({
  useChatSource = useRecoveryChat,
  onAllScenariosComplete,
  onWizardPatch,
  holdGuidedPlayback = false,
}: Props) => {
  const [css] = useStyletron()
  const token = useSelector((state: { auth: Auth }) => state.auth.token || '')
  const merchantId = useSelector(
    (state: { auth: Auth }) => state.auth.current_merchant_id || 0
  )

  // Guided persona playback. complete() (the Complete CTA on the final
  // persona) fires onAllScenariosComplete so WP4's wizard can advance.
  const { state, activePersona, playbackKey, previous, next, complete } =
    usePersonaPlayback(onAllScenariosComplete)
  // Merge the persona's dossier onto its order_context so ONE `scenario` value
  // carries both into useSimulatorData's conversation-context builder and into
  // OrderDetailsCard/ScenarioDetailPanel — rather than threading a second prop
  // in parallel with this one everywhere it already flows.
  //   context: memoized so this object keeps a STABLE identity across renders
  //   that don't change persona — a fresh `{...}` every render would defeat
  //   useSimulatorData's `conversationContext` useMemo, which has `scenario`
  //   in its dep array (useSimulatorData.ts), rebuilding conversationContext
  //   (and re-triggering everything downstream of it) on every WizardSimulator
  //   render instead of only on an actual persona switch. `activePersona`
  //   alone is a sufficient dep: usePersonaPlayback returns
  //   `ACTIVE_PERSONA_SCENARIOS[index]`, a stable reference into a
  //   module-level constant array, so it only changes identity when the
  //   index does.
  const scenario: MockOrderScenario = useMemo(
    () => ({
      ...activePersona.order_context,
      dossier: activePersona.dossier,
    }),
    [activePersona]
  )

  // AI-162: two-phase testing. 'guided' walks the active scripted personas (with the
  // bottom PlaybackNav); finishing the last one enters 'free' — the "Test your
  // own" phase where the merchant chats freely and then clicks the wizard's
  // Continue to advance. The second coach tour overlays this free phase.
  const [phase, setPhase] = useState<'guided' | 'free'>('guided')
  const isFree = phase === 'free'

  // AI-739 review: the free "Test your own" phase must not stay anchored to the
  // last guided persona's order — complete() doesn't move the index, so `scenario`
  // is still persona 3's order here. Null it for the chat/context/engine; the
  // shared builder then substitutes the free-chat stand-in (freeChatOrder.ts), so
  // the brain gets a neutral anchor rather than a stale persona. The persona-label
  // heading still reads `scenario`.
  const chatScenario = isFree ? null : scenario

  // preActivation: true relaxes the configure-first gate so the engine renders
  // before the merchant activates (no onboarding_completed / approved rewards
  // required) — the whole point of testing the agent during onboarding.
  const {
    config,
    approvedRewards,
    conversationContext,
    cannedGreeting,
    isGreetingAdapting,
    error,
    ready,
    retry,
  } = useSimulatorData({
    preActivation: true,
    scenario: chatScenario,
  })

  // Same Rules-of-Hooks concern as StandaloneSimulator: the chat source is
  // invoked as a hook, so it must be a stable reference for the instance lifetime.
  const initialChatSourceRef = useRef(useChatSource)
  const hasWarnedChatSourceSwapRef = useRef(false)
  if (
    process.env.NODE_ENV !== 'production' &&
    !hasWarnedChatSourceSwapRef.current &&
    initialChatSourceRef.current !== useChatSource
  ) {
    hasWarnedChatSourceSwapRef.current = true
    // eslint-disable-next-line no-console
    console.error(
      '[WizardSimulator] `useChatSource` changed across renders. It is called ' +
        'as a hook and must be a stable reference for the component lifetime ' +
        '(Rules of Hooks). Key the component on the toggle instead of swapping ' +
        'the prop on a mounted instance.'
    )
  }

  const live = useChatSource({
    token,
    merchantId,
    scenario: chatScenario,
    conversationContext,
    // Always open now. The guided scenarios used to be fully canned (no socket,
    // no brain, no cost); they are live conversations again, with only the
    // customer's turns scripted, so every phase needs the socket. The accepted
    // trade is 3 real conversations per merchant during onboarding in exchange
    // for replies that actually reflect the settings they just chose.
    enabled: true,
  })

  // The guided phase: a live conversation whose CUSTOMER turns the shell sends on
  // the merchant's behalf. The free phase hands the keyboard back, so this is
  // simply "not free" — there is no third mode.
  //
  //   context: this used to be gated on `recovery-ai-scripted-demo` as well.
  //   That flag is gone (see the PR description): it never gated anything
  //   server-side — thanx-ai does not read it, so `recovery-ai` was always the
  //   real kill switch — and keeping it meant maintaining a second guided
  //   experience (one live opener, merchant types) that nobody wants to ship.
  //   Its name had also come to mean the opposite of what it did: ON selected
  //   the LIVE conversation.
  const isCannedLive = !isFree

  const {
    messages,
    currentStreamingMessage,
    resolutionData,
    escalationData,
    isToolRunning,
    awaitingResponse,
    connectionError,
    sendMessage,
    reset,
    restart,
    isConnected,
  } = live

  // Declared above useCannedConversation because it feeds that call — a settings
  // save restarts the conversation, and this is what tells the script to replay
  // rather than sit on a spent cursor.
  const [guidedRunId, setGuidedRunId] = useState(0)
  // `restart`, NOT `reset`. reset() leaves isConnected false with nothing to raise
  // it, and the guided turns are gated on isConnected — so a plain reset here
  // strands the phase permanently blank. That hazard is exactly why this used to be
  // excluded from the guided phase; restart() pairs the clear with a re-subscribe,
  // which is what a persona change was doing implicitly all along.
  // Set when a save commits, cleared when the restart actually drains. Covers the
  // window the regen flag cannot: usePendingConversationReset defers the restart
  // while a turn is in flight, and during that wait the conversation on screen is
  // already invalid. Paired with isGreetingAdapting below — neither term alone
  // spans the whole "applying" period, because a non-brand-voice save (posture,
  // rewards) triggers no canned rebuild at all and so never raises that flag.
  const [restartPending, setRestartPending] = useState(false)
  const restartConversation = useCallback(() => {
    restart()
    setGuidedRunId(previous => previous + 1)
    setRestartPending(false)
  }, [restart])

  // Blank the preview and HOLD the script for as long as a save is still landing.
  // Two terms, because neither covers the period alone:
  //   restartPending      save committed, conversation not yet reloaded (deferred
  //                       while a turn is in flight). The only term a
  //                       posture/rewards save ever raises.
  //   isGreetingAdapting  the canned-response rebuild — one LLM call, so this is
  //                       the long pole (~13s) on a brand-voice save specifically,
  //                       and it outlives the restart.
  // Declared above useCannedConversation because it gates that hook — see the
  // `enabled` term below for why the pause is the half that makes the blank honest.
  // Deliberately NOT extended over the replay itself: once turns start arriving the
  // merchant wants to WATCH the new conversation, and hiding it then would hide the
  // very thing they are waiting for.
  const settingsApplying = restartPending || isGreetingAdapting

  // Drives this persona's scripted customer turns into the live agent, waiting
  // for each reply before asking the next thing. Called unconditionally beside
  // useChatSource — never swapped for it, which would re-key and reset the free
  // phase.
  //   context: `ready` is in the gate because the two early returns below render
  //   SimulatorStatus instead of the engine, and a turn must not fire behind the
  //   spinner.
  //   context: holdGuidedPlayback is the tour-sequencing term — the conversation
  //   must not start moving before the merchant has asked for it, and Testing.tsx
  //   owns when that is. Honoured here, unlike master's live guided phase, which
  //   deliberately ignored it: there the hold left the phone genuinely EMPTY
  //   under a tour step captioned "What your customer sees". It no longer does —
  //   `pinGreeting` keeps the agent's opening line on screen throughout — so
  //   holding now costs nothing and stops the scenario running underneath the
  //   tour.
  useCannedConversation({
    personaId: activePersona.persona_id,
    // Re-arms the cursor after a settings save restarts the conversation, so the
    // scenario replays under the new settings instead of stopping on a spent
    // script. Without it the restart clears the log and nothing refills it.
    runId: guidedRunId,
    //   context: settingsApplying is the same SHAPE of hold as holdGuidedPlayback —
    //   "the conversation must not move yet" — and is what makes the blank preview
    //   honest rather than cosmetic. Without it the script keeps sending behind the
    //   blanked screen, so the merchant would be shown a finished conversation
    //   generated under a voice that had not landed when its turns went out. The
    //   hook's effect clears its pending beat whenever `enabled` drops, so nothing
    //   is in flight across the window and nothing double-fires when it lifts.
    enabled:
      isCannedLive &&
      ready &&
      !error &&
      !holdGuidedPlayback &&
      !settingsApplying,
    isConnected,
    awaitingResponse,
    currentStreamingMessage,
    isToolRunning,
    resolutionData,
    escalationData,
    messages,
    sendMessage,
  })

  // The single-opener auto-send that used to run with the flag OFF is gone with
  // the flag. `useCannedConversation` above sends that same persona opener as
  // turn 1, so the two paths were mutually exclusive by construction and only one
  // of them was ever the intended behaviour.

  // The scripted 4-step checklist is gone from this surface. Its reveal was
  // gated on `revealAfterRow` against the canonical transcript's row numbers,
  // which no longer exist once the agent's half of the conversation is real —
  // and narrating a fixed explanation over a reply that may not match it is
  // worse than no narration. Passing nothing drops SimulatorEngine back to the
  // live DecisionLogPanel, which is what the free phase already shows under the
  // same "AI reasoning" heading (decisionLogVariant="wizard").
  // The config card lives in the engine's right column; its open row is held here
  // so the card's chevron rows and the reasoning "Tweak …" links share one row.
  const [openRow, setOpenRow] = useState<RowKey | null>(null)

  // Mirror a live save back into the wizard draft. Finalize re-sends
  // philosophy_posture and approved_reward_ids UNCONDITIONALLY from state that
  // hydrates only once, so without this an edit made here would be silently
  // overwritten by the stale value a few steps later.
  // BOTH PHASES now. This was free-phase-only, for two reasons that no longer hold:
  //   • "the guided transcript is a frozen script that never responded to settings, so
  //     replaying it conveys nothing" — true of the scripted demo, obsolete since the
  //     guided phase became live (only the CUSTOMER turns are scripted). Replaying is
  //     now the whole point: the agent answers again under the new settings.
  //   • "a reset here strands the merchant on a PERMANENTLY blank guided chat" — still
  //     completely true of reset(), and the reason restartConversation above uses
  //     restart() plus a runId bump. Two independent things had to be fixed for this:
  //     the socket (reset drops isConnected with nothing to re-raise it) and the
  //     script cursor (spent turns + an empty log ⇒ neither scriptedTurnDue nor
  //     closingDue can ever fire again).
  // What is deliberately NOT preserved: the merchant's watch position within the
  // scenario. A save invalidates the replies already on screen, so keeping their place
  // would just mean showing stale ones — the scenario restarts from turn 1. Their
  // position in the scenario PAGER is untouched.
  // Carries the same nexus #1378 backend dependency as the standalone shell — see
  // usePendingConversationReset's docstring for what breaks before it ships.
  //
  // live, NOT the selected source: a scripted transcript has an awaitingResponse of its
  // own, and deferring a free-phase reset behind a turn the merchant is only watching
  // would be the wrong reason. Wider than resetDisabled below: awaitingResponse clears on
  // the FIRST text_chunk, so on that term alone a save mid-reply would drain immediately
  // and wipe the streaming reply. See StandaloneSimulator for why the manual reset stays
  // narrow.
  const turnInFlight =
    live.awaitingResponse ||
    live.currentStreamingMessage !== null ||
    live.isToolRunning
  // Both phases restart on a save — see the hook's docstring for the nexus #1378
  // deploy dependency this relies on.
  const armConversationReset = usePendingConversationReset({
    reset: restartConversation,
    blocked: turnInFlight,
  })

  const handleCommitted = useCallback(
    (staged: Partial<RecoveryConfig>) => {
      const patch: Partial<WizardState> = {}
      if (staged.philosophy_posture !== undefined) {
        patch.philosophyPosture = staged.philosophy_posture
        // Mark it merchant-chosen so re-running the Train assessment doesn't
        // re-apply its recommendation over this deliberate edit.
        patch.philosophyPostureOverridden = true
      }
      if (staged.approved_reward_ids !== undefined) {
        patch.selectedRewardIds = staged.approved_reward_ids
      }
      // Any successful save here changes the agent the merchant just tested, so
      // the "tested" certification has to be re-earned. Reset it explicitly
      // rather than leaning on WizardContext's re-arm effect: that watches
      // wizard-state slices, and brand voice — editable from this card — has no
      // such slice, so it would otherwise Continue on a stale certification.
      onWizardPatch?.({ ...patch, testingComplete: false })
      // Same idea carried to the transcript: the agent the merchant just tested has
      // changed, so the conversation is reloaded and the phone starts from a blank
      // slate under the new settings. Applies in the GUIDED phase too: the scenario
      // replays from turn 1 so every reply is the new agent's, not just the greeting.
      armConversationReset()
      // Grey the preview from the moment the save lands.
      setRestartPending(true)
    },
    [onWizardPatch, armConversationReset]
  )

  // Re-arm the tested-agent certification when the merchant actively tests in the
  // FREE phase. A live config save clears testingComplete (handleCommitted above),
  // but the free phase has no PlaybackNav to re-complete and setPhase('free') is
  // one-way — so without this a post-guided save would leave the wizard's Continue
  // permanently disabled with no way back (Darshan review, #3878). Sending a
  // message here IS the re-test, so it re-certifies. Guided-phase sends must NOT:
  // testingComplete is earned there only by completing all three personas.
  const handleMerchantSend = useCallback(
    (text: string) => {
      if (isFree) onWizardPatch?.({ testingComplete: true })
      sendMessage(text)
    },
    [isFree, onWizardPatch, sendMessage]
  )

  // Switching personas resets the chat; the engine is also keyed on playbackKey
  // so a switch remounts it cleanly.
  const handlePrevious = useCallback(() => {
    reset()
    previous()
  }, [reset, previous])
  const handleNext = useCallback(() => {
    reset()
    next()
  }, [reset, next])

  // Finishing the last guided persona enters the free "Test your own" phase with
  // an empty conversation, and fires onAllScenariosComplete so the wizard's
  // Continue unlocks — the merchant tests freely, then clicks Continue to advance.
  const handleComplete = useCallback(() => {
    reset()
    setPhase('free')
    complete()
  }, [reset, complete])

  // AI-583 recovery: a terminal state (reward granted OR escalation) locks the
  // shared compose box. The guided phase recovers via PlaybackNav (each nav
  // action calls reset()), but the free "Test your own" phase has no PlaybackNav
  // — so without this the first grant would lock the box for the rest of the
  // step. reset() clears resolutionData/escalationData, clearing the lock.
  const handleNewConversation = useCallback(() => reset(), [reset])

  // The reset link renders for the WHOLE free phase, not only once there is
  // something to clear. It used to be gated on real conversation state so it was
  // never a visible no-op, but that made it appear after the merchant's first
  // exchange, which added a row above the phone and shifted the device. A reset
  // over an empty chat is harmless (handleNewConversation is just reset()), so the
  // stable layout is worth more than avoiding the no-op.
  //
  // What survives from that gate is the part that is a correctness guard rather
  // than a cosmetic one: never mid-turn. Resetting with a reply inbound leaks that
  // turn into the next conversation, so the control is disabled — not unmounted,
  // which would move the phone again.
  //   context: read from `live`, not the selected chat — a scripted transcript
  //   always has an awaitingResponse of its own, which would disable the free
  //   phase's control for the wrong reason.
  const resetDisabled = live.awaitingResponse

  // ENG-969: the wizard no longer renders the persona-scripted "What your agent
  // did" panel — per Figma the testing surface is Agent settings + AI reasoning
  // only — so this shell passes the scripted reasoning checklist instead and the
  // engine mounts no ReasoningPanel. The in-tab / standalone shells are
  // unaffected.

  // A failed config/rewards GET must NOT fall through to the spinner: the wizard
  // has no Exit chrome, so a stuck spinner during onboarding is unrecoverable.
  // Surface the recoverable error region instead (AI-95 review).
  if (error) {
    return <SimulatorStatus variant="error" onRetry={retry} />
  }

  // Gate on `ready`, NOT isLoading: with preActivation:true, ready === !isLoading,
  // so this shows the spinner during the config GET and the engine once loaded.
  // Gating on `ready` (rather than isLoading) makes the preActivation flag
  // load-bearing — were it false, an unconfigured merchant would stay !ready and
  // never reach the engine, which the pre-activation test asserts against.
  if (!ready) {
    return <SimulatorStatus variant="loading" />
  }

  // The engine is keyed on playbackKey so switching personas remounts (clears)
  // it; the PlaybackNav lives OUTSIDE that key as the wizard's bottom nav bar
  // (per Figma — dots at the bottom, no dark chrome), so it persists across
  // switches.
  // AI-162: the phone-mockup layout replaces the left ScenarioDetailPanel with a
  // centered header above the phone. ENG-971 splits that header into a pager
  // chip ("Scenario 1 of 3") plus the scenario NAME, which now comes from the
  // persona's i18n name_key rather than the order-context data. ENG-978: the
  // free "test your own" phase carries no header text at all — just the reset
  // link — so both parts are undefined there.
  // No pager chip in the free phase — "Scenario 2 of 3" is meaningless once the
  // merchant is driving, and the Figma shows the title alone there.
  const scenarioChip = isFree
    ? undefined
    : t('scenario_chip', { number: state.index + 1, total: state.total })
  // The free phase DOES carry a title (reversing ENG-978). Without one, every
  // header part went undefined at once on the transition, SimulatorEngine's
  // showCenterHeader went false, and the header unmounted — dropping the phone
  // upward by its full height, then back down when the reset link reappeared.
  const scenarioHeading = isFree
    ? t('free_phase_heading')
    : tPersonas(activePersona.profile.name_key)

  // Re-key the engine when entering the free phase so it remounts with a clean
  // conversation (reset() also clears the chat-source messages on the transition).
  const engineKey = isFree ? 'free' : playbackKey

  return (
    <div>
      <SimulatorEngine
        settingsCard={
          <AgentTuningConfigModals
            openRow={openRow}
            onOpenRow={setOpenRow}
            hideAdvancedRow
            hideSaveAndTest
            onCommitted={handleCommitted}
            // Both phases restart on save (see handleCommitted above).
            conversationRestartsOnSave
          />
        }
        key={engineKey}
        messages={messages}
        currentStreamingMessage={currentStreamingMessage}
        awaitingResponse={awaitingResponse}
        isToolRunning={isToolRunning}
        onSendMessage={handleMerchantSend}
        assistantName={config?.assistant_name}
        agentName={config?.agent_name}
        cannedGreeting={cannedGreeting}
        settingsApplying={settingsApplying}
        scenario={chatScenario}
        resolutionData={resolutionData}
        escalationData={escalationData}
        connectionError={connectionError}
        config={config}
        // AI-202 / AI-585: forward the grantable count AND the approvedRewards
        // array. The count feeds the settings summary ("N rewards"); the array
        // lets the inline offer match a reward_id to its enriched image. The
        // right-column SandboxRewardCard list stays hidden via
        // showRewardPreviews={false}. All derive from the one useSimulatorData
        // source, so they can't drift.
        rewardCount={approvedRewards.length}
        approvedRewards={approvedRewards}
        showRewardPreviews={false}
        chatVariant="phone"
        layoutVariant="wizard"
        scenarioHeading={scenarioHeading}
        scenarioChip={scenarioChip}
        // ENG-978: the free phase's reset moved from a bottom button to the
        // "Reset conversation" link above the phone. Present for the whole phase
        // so it pairs with "Test your own" as the free phase's two header rows,
        // matching the guided phase's chip + persona title. The guided phase
        // resets through PlaybackNav instead.
        onReset={isFree ? handleNewConversation : undefined}
        resetDisabled={resetDisabled}
        // The three guided personas are LIVE conversations the shell drives: the
        // merchant watches their own agent answer scripted customer turns, so the
        // compose box greys out rather than disappearing. `composeLocked` stays
        // scoped to that guided phase — the merchant is only watching then, so
        // locking the box is exactly this phase's rule. The free "Test your own"
        // phase hands the wheel back and unlocks it.
        //   context: no `scriptedDemo` / `scriptedOffer` / `offerAfterMessageId`.
        //   The reward card is whatever the agent actually granted, rendered from
        //   live resolutionData at the tail of the log like every other live
        //   surface — there is no canonical row to anchor it to any more.
        composeLocked={isCannedLive}
        // `pinGreeting` is unconditional — matching StandaloneSimulator's
        // always-pinned behavior. The assistant's opening line is real transcript
        // context, not guided-phase chrome: once the merchant starts typing in the
        // FREE phase the greeting must stay visible above the new exchange too, or
        // it silently vanishes from the log the moment the first reply lands
        // (AI-921 regression). Phase-scoping this was the bug, not the fix.
        pinGreeting
        onOpenConfigRow={setOpenRow}
        // AI-204: the wizard Figma variant has no dev-only sandbox-prompt pane,
        // titles the decision log "AI reasoning", and restyles the agent-settings
        // card (subtitle, "Recovery generosity", stacked rows).
        showSandboxPrompt={false}
        decisionLogVariant="wizard"
        agentSettingsVariant="wizard"
      />
      {/* Guided phase: the scenario pager (also the reset path — each nav action
          calls reset()). The free phase has no scenarios to step through and no
          bottom control at all — ENG-978 moved its reset up to the "Reset
          conversation" link above the phone. The merchant drives the chat, then
          advances via the wizard's Continue. */}
      {!isFree && (
        // AI-1123: pin the scenario pager to the bottom of the viewport so the
        // Next/Complete button stays reachable while the merchant scrolls the
        // taller simulator content. grey05 fill + grey20 top border read it as a
        // bar; the top padding keeps the buttons off the scrolling content behind.
        <div
          data-testid="wizard-playback-bar"
          className={css({
            position: 'sticky',
            bottom: 0,
            zIndex: WIZARD_STICKY_BAR_Z_INDEX,
            backgroundColor: palette.grey05,
            borderTop: `1px solid ${palette.grey20}`,
            padding: '16px 0',
          })}
        >
          <PlaybackNav
            state={state}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onComplete={handleComplete}
          />
        </div>
      )}
    </div>
  )
}

export default WizardSimulator
